import OptimizedImage from '../common/OptimizedImage';
import { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { useEffect } from "react";

import "../../css/LandingPage.css";
import { LoginModal, VerifyOTPModal, ForgotPasswordModal, ResetPasswordModal } from "../auth/LazyAuthModals";
import { newsAPI, analyticsAPI } from "../../services/api";

const FALLBACK_NEWS = [
  {
    _id: "fallback-1",
    title: "PILAREÑOS: SK SPORTS LEAGUE 2026 — Orientation & Jersey Distribution",
    body: "It's officially game on. Calling all Pilareño athletes — gear up and join the orientation and jersey distribution. We'll cover the schedule, rules, and the up-and-down jerseys you'll be wearing all season.",
    imageUrl: "/images/SportsLeague.jpg",
    publishedAt: "2026-04-27",
  },
  {
    _id: "fallback-2",
    title: "Notice to Barangay Pilar Residents — PhilHealth & Medical Mission",
    body: "Good day. Barangay Pilar will host PhilHealth registration, updating, and ID processing, plus YAKAP registration and a free medical mission with PhilHealth and RMA Medical Clinic Corp.",
    imageUrl: "/images/Announcement.jpg",
    publishedAt: "2026-04-28",
  },
  {
    _id: "fallback-3",
    title: "Oplan: Libreng Tuli sa Barangay Pilar — Successful Turnout",
    body: "The Libreng Tuli Community Surgical Mission was completed at the Barangay Pilar Multipurpose Hall. Free service was provided to young Pilareños aged 10 and above.",
    imageUrl: "/images/OplanLibre.jpg",
    publishedAt: "2026-04-25",
  },
];

const formatNewsDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-PH", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return "";
  }
};

const truncate = (s = "", n = 220) => (s.length > n ? `${s.slice(0, n).trimEnd()}…` : s);

const LandingPage = () => {
  const [showLogin, setShowLogin] = useState(false);
  const [showVerify, setShowVerify] = useState(false);
  const [show2FA, setShow2FA] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [verifyEmail, setVerifyEmail] = useState("");
  const [verifyPassword, setVerifyPassword] = useState("");
  const [resetEmail, setResetEmail] = useState("");
  const [userId, setUserId] = useState("");
  const [newsPosts, setNewsPosts] = useState(FALLBACK_NEWS);
  const [publicStats, setPublicStats] = useState({ totalDrivers: 0, completedTrips: 0, totalRiders: 0 });

  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;
  const isHome = pathname === "/";
  const isJoinUs = pathname === "/become-rider";

  useEffect(() => {
    if (location.state?.openLogin && !isAuthenticated) {
      setShowLogin(true);
      navigate(pathname, { replace: true, state: {} });
    }
  }, [location.state, isAuthenticated, navigate, pathname]);

  useEffect(() => {
    let cancelled = false;
    newsAPI
      .getWebPosts()
      .then((res) => {
        if (cancelled) return;
        const posts = res?.data?.data || [];
        if (Array.isArray(posts) && posts.length > 0) setNewsPosts(posts.slice(0, 3));
      })
      .catch(() => {});
    analyticsAPI
      .getPublicStats()
      .then((res) => {
        if (cancelled) return;
        const s = res?.data?.data;
        if (s) setPublicStats(s);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const handleOpenVerify = (email, password = "") => {
    setVerifyEmail(email);
    setVerifyPassword(password);
    setShowVerify(true);
  };

  const handleOpen2FA = (id) => {
    setUserId(id);
    setShow2FA(true);
  };

  const handleOpenForgotPassword = () => {
    setShowForgotPassword(true);
  };

  const handleOpenResetPassword = (email) => {
    setResetEmail(email);
    setShowResetPassword(true);
  };

  const handleBackToLogin = () => {
    setShowLogin(true);
  };

  const handleBecomeRider = () => {
    navigate("/become-rider");
  };

  const handleBookRider = () => {
    navigate("/book-rider");
  };

  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 968) setMenuOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);

  return (
    <div className="landing-page">
      <nav className="navbar" role="navigation" aria-label="Main navigation">
        <div className="container">
          <a className="logo" aria-label="ToDaGo home" href="/">
            <OptimizedImage
              className="logo-desktop"
              src="/images/web_todago_logo.png"
              alt="ToDaGo Logo"
              width="1768"
              height="520"
              decoding="async"
            />
            <OptimizedImage
              className="logo-mobile"
              src="/images/App_Logo_Tricycle.png"
              alt="ToDaGo Logo"
              width="2048"
              height="2048"
              decoding="async"
            />
          </a>

          <ul className="nav-links">
            <li>
              <a href="#home" className={isHome ? "active" : ""} aria-label="Go to home section">
                Home
              </a>
            </li>
            <li>
              <a href="/become-rider" className={isJoinUs ? "active" : ""} aria-label="Join us as a rider">
                Join Us
              </a>
            </li>
            <li>
              <a href="/about-us" aria-label="About ToDaGo">
                About Us
              </a>
            </li>
          </ul>

          <div className="auth-buttons">
            {isAuthenticated ? (
              <>
                <span className="welcome-text">Welcome, {user?.username}!</span>
                <button
                  className="btn-login"
                  onClick={() => navigate("/admin")}
                  aria-label="Go to the admin dashboard"
                >
                  Dashboard
                </button>
                <button
                  onClick={logout}
                  className="btn-logout"
                  aria-label="Log out of your account"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <span className="nav-app-hint">New here? Download the app</span>
                <button
                  className="btn-login"
                  onClick={() => setShowLogin(true)}
                  aria-label="Log in to your account"
                >
                  Log In
                </button>
              </>
            )}
          </div>

          <button
            className={`hamburger${menuOpen ? " is-open" : ""}`}
            onClick={() => setMenuOpen((o) => !o)}
            aria-label={
              menuOpen ? "Close navigation menu" : "Open navigation menu"
            }
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
          >
            <span className="ham-bar" />
            <span className="ham-bar" />
            <span className="ham-bar" />
          </button>
        </div>

        <div
          id="mobile-menu"
          className={`mobile-menu${menuOpen ? " is-open" : ""}`}
          aria-hidden={!menuOpen}
          inert={!menuOpen ? '' : undefined}
        >
          <ul className="mobile-nav-links">
            <li>
              <a href="#home" onClick={closeMenu}>
                Home
              </a>
            </li>
            <li>
              <a href="/become-rider" onClick={closeMenu}>
                Join Us
              </a>
            </li>
            <li>
              <a href="/about-us" onClick={closeMenu}>
                About Us
              </a>
            </li>
          </ul>
          <div className="mobile-auth">
            {isAuthenticated ? (
              <>
                <button
                  className="btn-login"
                  onClick={() => {
                    navigate("/admin");
                    closeMenu();
                  }}
                >
                  Dashboard
                </button>
                <button
                  className="btn-logout"
                  onClick={() => {
                    logout();
                    closeMenu();
                  }}
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <p className="mobile-app-hint">New here? Download the ToDaGo app.</p>
                <button
                  className="btn-login"
                  onClick={() => {
                    setShowLogin(true);
                    closeMenu();
                  }}
                >
                  Log In
                </button>
              </>
            )}
          </div>
        </div>

        {menuOpen && (
          <div
            className="mobile-overlay"
            onClick={closeMenu}
            aria-hidden="true"
          />
        )}
      </nav>

      <main>
      <section className="hero" id="home">
        <div className="container hero-container">
          <div className="hero-content">
            <div className="hero-eyebrow">
              <span className="hero-eyebrow-dot" />
              Community-powered rides in Pilar Village
            </div>
            <h1>
              Your neighborhood ride,
              <span className="highlight"> ready when you are.</span>
            </h1>
            <p>
              Book verified local tricycle drivers, view your fare before the
              trip, and follow every ride from pickup to destination.
            </p>
            <div className="hero-buttons">
              <button
                className="btn-primary"
                onClick={handleBookRider}
              >
                Get the ToDaGo App
              </button>
              <button
                className="btn-secondary"
                onClick={handleBecomeRider}
              >
                Join Our Community
              </button>
            </div>
            <div className="hero-trust" aria-label="ToDaGo service benefits">
              <span><i />Verified local drivers</span>
              <span><i />Upfront fare estimates</span>
              <span><i />Live trip updates</span>
            </div>
          </div>
          <div className="hero-image">
            <div className="hero-orbit hero-orbit-one" />
            <div className="hero-orbit hero-orbit-two" />
            <div className="hero-visual-card">
              <OptimizedImage
                src="/images/TricycleLandingPageUpd.png"
                alt="ToDaGo tricycle illustration"
                width="500"
                height="500"
                loading="eager"
                fetchPriority="high"
                decoding="async"
              />
            </div>
            <div className="hero-float-card hero-float-card-top">
              <span className="float-icon">01</span>
              <span><strong>Verified drivers</strong><small>Trusted local operators</small></span>
            </div>
            <div className="hero-float-card hero-float-card-bottom">
              <span className="float-icon">24</span>
              <span><strong>Easy booking</strong><small>Ride when you need it</small></span>
            </div>
          </div>
        </div>
      </section>

      <section className="proof-strip" aria-label="ToDaGo at a glance">
        <div className="container proof-strip-inner">
          <div className="proof-stat">
            <div className="proof-num">{Number(publicStats.completedTrips || 0).toLocaleString('en-PH')}</div>
            <div className="proof-label">Completed Trips</div>
          </div>
          <div className="proof-divider" aria-hidden="true" />
          <div className="proof-stat">
            <div className="proof-num">{Number(publicStats.totalDrivers || 0).toLocaleString('en-PH')}</div>
            <div className="proof-label">Verified Drivers</div>
          </div>
          <div className="proof-divider" aria-hidden="true" />
          <div className="proof-stat">
            <div className="proof-num">{Number(publicStats.totalRiders || 0).toLocaleString('en-PH')}</div>
            <div className="proof-label">Registered Riders</div>
          </div>
          <div className="proof-divider" aria-hidden="true" />
          <div className="proof-stat">
            <div className="proof-num">Pilar Village</div>
            <div className="proof-label">Service Area, Las Piñas</div>
          </div>
        </div>
      </section>

      <section className="benefits" id="services">
        <div className="container">
          <div className="section-heading">
            <span className="section-eyebrow">Built for everyday travel</span>
            <h2>A better way to move around your community</h2>
            <p>Familiar local tricycle service, made safer, clearer, and easier through one dependable app.</p>
          </div>
          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon-wrap">
                <OptimizedImage
                  src="/images/Your_Local_Tricycle_Now_Digital.png"
                  sizes="46px"
                  alt=""
                  aria-hidden="true"
                  width="500"
                  height="500"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <h3>Your Local Tricycle, Now Digital</h3>
              <p>
                Modernizing ATODA tricycle services in Pilar Village, Las Piñas
                City
              </p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon-wrap">
                <OptimizedImage
                  src="/images/Book_a_Ride_in_Seconds.png"
                  sizes="46px"
                  alt=""
                  aria-hidden="true"
                  width="500"
                  height="500"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <h3>Book a Ride in Seconds</h3>
              <p>
                Request a nearby tricycle from your phone, no more waiting at
                terminals
              </p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon-wrap">
                <OptimizedImage
                  src="/images/Know_Your_Driver.png"
                  sizes="46px"
                  alt=""
                  aria-hidden="true"
                  width="500"
                  height="500"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <h3>Know Your Driver Before You Ride</h3>
              <p>
                Every driver is verified and ATODA-accredited for your peace of
                mind
              </p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon-wrap">
                <OptimizedImage src="/images/Transparen_fare.png" sizes="46px" alt="" aria-hidden="true" width="500" height="500" loading="lazy" decoding="async" />
              </div>
              <h3>Transparent Fares, No Surprises</h3>
              <p>
                See your estimated fare before confirming, honest pricing every
                trip
              </p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon-wrap">
                <OptimizedImage src="/images/Voice_command.png" sizes="46px" alt="" aria-hidden="true" width="500" height="500" loading="lazy" decoding="async" />
              </div>
              <h3>Voice Command Ready</h3>
              <p>
                Book a ride hands-free using just your voice, perfect for
                seniors and PWDs
              </p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon-wrap">
                <OptimizedImage src="/images/Safe_rides.png" sizes="46px" alt="" aria-hidden="true" width="507" height="493" loading="lazy" decoding="async" />
              </div>
              <h3>Safer Rides, Every Time</h3>
              <p>
                Emergency contacts, trip history, and lost item recovery built
                right in
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="news-section" id="news">
        <div className="container">
          <div className="section-heading section-heading-left">
            <span className="section-eyebrow">Community updates</span>
            <h2>What is happening around ToDaGo</h2>
            <p>Stay informed about local programs, service announcements, and community events.</p>
          </div>
          <div className="news-grid">
            {newsPosts.map((post) => {
              const isReal = post._id && !String(post._id).startsWith("fallback-");
              const cardProps = isReal
                ? {
                    onClick: () => navigate(`/news/${post._id}`),
                    role: "button",
                    tabIndex: 0,
                    onKeyDown: (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate(`/news/${post._id}`);
                      }
                    },
                    style: { cursor: "pointer" },
                  }
                : {};
              return (
                <article key={post._id} className="news-card" {...cardProps}>
                  <div className="news-img-wrap">
                    <OptimizedImage
                      src={post.imageUrl || "/images/Announcement.jpg"}
                      alt={post.title}
                      width="1536"
                      height="1024"
                      loading="lazy"
                      decoding="async"
                    />
                    <div className="news-img-overlay" aria-hidden="true" />
                    <span className="news-category">{post.category || "Community"}</span>
                  </div>
                  <div className="news-content">
                    <div className="news-meta">
                      <span className="news-date">{formatNewsDate(post.publishedAt || post.createdAt)}</span>
                      <span className="news-meta-dot" aria-hidden="true" />
                      <span>ToDaGo update</span>
                    </div>
                    <h3>{post.title}</h3>
                    <p>{truncate(post.body)}</p>
                    {isReal && (
                      <span className="news-read-more">
                        Read article <span aria-hidden="true">&rarr;</span>
                      </span>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="download-cta">
        <div className="container">
          <div className="download-cta-card">
            <div className="download-cta-copy">
              <span className="section-eyebrow">Your next ride is closer</span>
              <h2>Travel around Pilar Village with confidence.</h2>
              <p>Download ToDaGo and connect with verified community drivers from your phone.</p>
            </div>
            <button className="download-cta-button" onClick={handleBookRider}>
              Download the App
            </button>
          </div>
        </div>
      </section>

      </main>
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <OptimizedImage src="/images/web_todago_logo.png" alt="ToDaGo" width="1768" height="520" loading="lazy" decoding="async" />
              <p>Reliable community rides, powered by local drivers.</p>
            </div>
            <div className="footer-links">
              <a href="#home">Home</a>
              <a href="/become-rider">Join Us</a>
              <a href="/about-us">About Us</a>
            </div>
          </div>
          <div className="footer-bottom">
            <span>&copy; 2026 ToDaGo Tricycle Booking. All rights reserved.</span>
            <span>Pilar Village, Las Piñas City</span>
          </div>
        </div>
      </footer>

      <LoginModal
        isOpen={showLogin}
        onClose={() => setShowLogin(false)}
        onOpenVerify={handleOpenVerify}
        onOpen2FA={handleOpen2FA}
        onOpenForgotPassword={handleOpenForgotPassword}
      />

      <VerifyOTPModal
        isOpen={showVerify}
        onClose={() => {
          setShowVerify(false);
          setVerifyPassword("");
        }}
        email={verifyEmail}
        password={verifyPassword}
      />

      <VerifyOTPModal
        isOpen={show2FA}
        onClose={() => setShow2FA(false)}
        userId={userId}
        is2FA={true}
      />

      <ForgotPasswordModal
        isOpen={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
        onOpenResetPassword={handleOpenResetPassword}
      />

      <ResetPasswordModal
        isOpen={showResetPassword}
        onClose={() => setShowResetPassword(false)}
        email={resetEmail}
        onBackToLogin={handleBackToLogin}
      />
    </div>
  );
};

export default LandingPage;
